/**
 * The storm notices' seam (decision 692; docs/specs/space-weather.md §6): which "Storm watch" and "Storm now"
 * events the space weather of one moment raises for one project, each with a stable key so it is sent once per event.
 * There is no "Storm over": the crew review the conditions and decide (decision 692).
 *
 * This module only decides; it sends nothing. Teslyn Cloud's own check (`space-weather-check.ts`, Edge Function
 * `space-weather-poller`, R115) calls `stormAlertEvents` for each Mobilising and In progress project with the
 * project's Storm level, keeps one notice per storm (an episode) and passes it to `alert_raise`, whose
 * `alert_event_key` keeps one event per key; `notify` sends it as each person's Alerts choices say. Teslyn Field and
 * Teslyn Cloud show the same events on their Space weather pages, so the pages and the notices never disagree.
 */
import type { BomIndex, BomMagAlert, BomMagWarning, NoaaMessage } from './space-weather.js';

export type StormAlertKind = 'storm_watch' | 'storm_now';

/** One event, in the shape `alert_raise` takes (`kind`, `event_key`, `title`, `body`, `occurred_at`). */
export interface StormAlertEvent {
  readonly kind: StormAlertKind;
  /** `<kind>:<source>:<what>:<time or serial>`: the same event always has the same key. */
  readonly eventKey: string;
  readonly title: string;
  readonly body: string;
  /** ISO UTC. */
  readonly occurredAt: string;
  /**
   * The NOAA G level the event names: a NOAA watch's highest G in the next 48 hours, a BoM magnetic alert's G; null
   * when it names none (a BoM warning, the Australian K). Teslyn Cloud sends a Storm watch again when it rises
   * (decision 812).
   */
  readonly g: number | null;
}

export interface StormAlertInput {
  readonly nowMs: number;
  readonly project: { readonly name: string; readonly utcOffsetH: number; readonly zoneLabel: string };
  readonly bomWarning: BomMagWarning | null;
  readonly bomAlert: BomMagAlert | null;
  readonly kAus: BomIndex | null;
  /** NOAA's messages (`parseNoaaAlerts`), newest first. */
  readonly noaa: readonly NoaaMessage[];
  /**
   * Where "Storm now" starts for this project: the verdict's Storm level of its counting limits (`stormNowFrom`,
   * decision 750: Australian K 5 and a G1 alert for Plot 1, Plot 2 or a high-latitude base; K 6 and G2 for the 10 nT
   * chord rule alone). Left out: K 5 and G1, the strictest.
   */
  readonly stormFrom?: { readonly kAus: number; readonly g: number };
}

/** "Storm now" when the Australian region K reaches this and no project level is given (spec §6; decision 750). */
export const STORM_NOW_K_AUS = 5;
/** A NOAA watch counts when it names a day within this many hours (spec §6: "the next 48 hours"). */
export const STORM_WATCH_HOURS = 48;

const local = (iso: string, offsetH: number, zone: string): string => {
  const t = Date.parse(iso) + offsetH * 3_600_000;
  const d = new Date(t);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.toISOString().slice(11, 16)} ${zone}`;
};

/**
 * The events of this moment for one project: a BoM geophysical warning that covers today or later, or a NOAA G1+
 * watch for a day within 48 hours, is a "Storm watch" (a heads-up: G1 is Kp 5, Exceedances likely or more on every
 * rule of decision 750); a current BoM magnetic alert at the project's Storm G (`stormFrom`), or the Australian
 * region K at 5 or more, is "Storm now". Pure.
 */
export function stormAlertEvents(input: StormAlertInput): readonly StormAlertEvent[] {
  const out: StormAlertEvent[] = [];
  const { project, nowMs } = input;
  const at = (iso: string): string => local(iso, project.utcOffsetH, project.zoneLabel);
  const today = new Date(nowMs).toISOString().slice(0, 10);
  const w = input.bomWarning;
  if (w?.issueTime && (w.endDate ?? w.startDate ?? '') >= today) {
    out.push({
      kind: 'storm_watch',
      eventKey: `storm_watch:bom:warning:${w.issueTime}`,
      title: `Storm watch: ${project.name}`,
      body: `BoM geophysical warning issued ${at(w.issueTime)}${w.activity.length ? `: ${w.activity.map((a) => `${a.date.slice(8, 10)}/${a.date.slice(5, 7)} ${a.forecast}`).join('; ')}` : ''}. Open Space weather in Teslyn Field or Teslyn Cloud.`,
      occurredAt: w.issueTime,
      g: null,
    });
  }
  const horizon = nowMs + STORM_WATCH_HOURS * 3_600_000;
  const newestWatch = input.noaa.find((m) => m.magnetic && (m.kind === 'watch' || m.kind === 'cancel'));
  if (newestWatch && newestWatch.kind === 'watch') {
    const days = newestWatch.watchDays.filter(
      (d) =>
        d.g >= 1 &&
        Date.parse(`${d.date}T23:59:59Z`) >= nowMs &&
        Date.parse(`${d.date}T00:00:00Z`) <= horizon,
    );
    if (days.length) {
      const g = Math.max(...days.map((d) => d.g));
      out.push({
        kind: 'storm_watch',
        eventKey: `storm_watch:noaa:${newestWatch.productId}:${newestWatch.serial ?? newestWatch.issued}`,
        title: `Storm watch: ${project.name}`,
        body: `NOAA G${g} geomagnetic storm watch for ${days.map((d) => d.date).join(', ')} (UTC), issued ${at(newestWatch.issued)}. Open Space weather in Teslyn Field or Teslyn Cloud.`,
        occurredAt: newestWatch.issued,
        g,
      });
    }
  }
  const from = input.stormFrom ?? { kAus: STORM_NOW_K_AUS, g: 1 };
  const a = input.bomAlert;
  if (a?.startTime && (a.gScale ?? 0) >= from.g && (!a.validUntil || Date.parse(a.validUntil) >= nowMs)) {
    out.push({
      kind: 'storm_now',
      eventKey: `storm_now:bom:alert:${a.startTime}`,
      title: `Storm now: ${project.name}`,
      body: `BoM magnetic alert G${a.gScale}${a.description ? ` (${a.description})` : ''} from ${at(a.startTime)}. Check the base station and Diurnal QC.`,
      occurredAt: a.startTime,
      g: a.gScale ?? null,
    });
  }
  const k = input.kAus;
  if (k && k.index >= from.kAus && nowMs - Date.parse(k.validTime) <= 6 * 3_600_000) {
    out.push({
      kind: 'storm_now',
      eventKey: `storm_now:bom:k_aus:${k.validTime}`,
      title: `Storm now: ${project.name}`,
      body: `Australian region K ${k.index} for the 3 hours from ${at(k.validTime)}. Check the base station and Diurnal QC.`,
      occurredAt: k.analysisTime ?? k.validTime,
      g: null,
    });
  }
  return out;
}
